import { Component, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DsButtonVariants, DsModalRef, DsModalService, DsModalVariant, Icons } from '@levelaccess/design-system';
import { $userApiKey, createApiKeyResult } from '../../../../../../shared/constants/user-api-key';
import { TranslateService } from '../../../../translate/translate.service';
import { NotificationService } from '../../../../services/notification.service';
import { IntegrationsService } from '../../../../services/integrations.service';
import { IGenerateKeyResult, IPreviousTokensServerResponse } from '../../../../../../shared/interfaces/user-api-key.interface';
import { AmpApiKeyModalComponent } from '../amp-api-key-modal/amp-api-key-modal.component';
import { NotificationPosition } from '../../../../models/notification.model';
import { ApikeyDeleteConfirmationComponent } from '../../../../pages/public/integrations/api/apikey-delete-confirmation/apikey-delete-confirmation.component';
import { SharedDateUtility } from '../../../../../../shared/utils/date.utility';

@Component({
  selector: 'app-amp-api-key',
  templateUrl: './amp-api-key.component.html',
  styleUrls: ['./amp-api-key.component.scss'],
  standalone: false,
})
export class AmpApiKeyComponent implements OnInit, OnDestroy {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public readonly $userApiKey: typeof $userApiKey = $userApiKey;

  public subscription: Subscription = new Subscription();
  public currentAmpApiKey$: BehaviorSubject<IPreviousTokensServerResponse | null> = new BehaviorSubject(null);

  @Input()
  public tenantId: string;

  public constructor(
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private dsModalService: DsModalService,
    private integrationsService: IntegrationsService,
    private viewContainerRef: ViewContainerRef,
  ) {}

  public ngOnInit(): void {
    this.getCurrentAmpApiKey();
  }

  public formatDate(date: string | Date): string {
    return SharedDateUtility.getDateAtTime(new Date(date));
  }

  private getCurrentAmpApiKey(): void {
    this.subscription.add(
      this.integrationsService.getAmpApiToken(this.tenantId).subscribe({
        next: this.onGetAmpApiTokenSuccess.bind(this),
        error: this.onGetAmpApiTokenFailed.bind(this),
      }),
    );
  }

  private onGetAmpApiTokenSuccess(result: IPreviousTokensServerResponse): void {
    this.currentAmpApiKey$.next(result);
  }

  private onGetAmpApiTokenFailed(): void {
    const message: string = this.translateService.instant('error_get_amp_api_key');
    this.notificationService.error(message, NotificationPosition.Toast, true);
  }

  public generateAmpApiKey(): void {
    this.subscription.add(
      this.integrationsService.generateAmpApiToken(this.tenantId).subscribe({
        next: this.onAmpApiKeyCreationSuccess.bind(this),
        error: this.onAmpApiKeyCreationFailed.bind(this),
      }),
    );
  }

  private onAmpApiKeyCreationSuccess(result: IGenerateKeyResult): void {
    const modalRef: DsModalRef = this.dsModalService.open(AmpApiKeyModalComponent, DsModalVariant.center);
    const modalComponent: AmpApiKeyModalComponent = modalRef.componentInstance as AmpApiKeyModalComponent;

    modalComponent.init({
      key: result[createApiKeyResult.key],
      keyName: result[createApiKeyResult.name],
      expiry: result[createApiKeyResult.expiry],
    });

    this.currentAmpApiKey$.next({
      [$userApiKey.name]: result[createApiKeyResult.name],
      [$userApiKey.expiry]: result[createApiKeyResult.expiry],
      [$userApiKey.createdAt]: result[createApiKeyResult.createdAt],
    });
  }

  private onAmpApiKeyCreationFailed(): void {
    const message: string = this.translateService.instant('error_generate_amp_api_key');
    this.notificationService.error(message, NotificationPosition.Toast, true);
  }

  public deleteAmpApiKey(): void {
    const modalComponentRef: ApikeyDeleteConfirmationComponent = this.viewContainerRef.createComponent(
      ApikeyDeleteConfirmationComponent,
    ).instance;
    this.subscription.add(
      modalComponentRef.confirmed.pipe(switchMap(() => this.integrationsService.deleteAmpApiToken(this.tenantId))).subscribe({
        next: this.onAmpApiKeyDeletionSuccess.bind(this),
        error: this.onAmpApiKeyDeletionFailed.bind(this),
      }),
    );
  }

  private onAmpApiKeyDeletionSuccess(): void {
    this.currentAmpApiKey$.next(null);
  }

  private onAmpApiKeyDeletionFailed(): void {
    const message: string = this.translateService.instant('error_delete_amp_api_key');
    this.notificationService.error(message, NotificationPosition.Toast, true);
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
